import React, { useEffect, useRef } from 'react';
import L from 'leaflet';
import { MapContainer, TileLayer, Polyline, Marker, useMap, CircleMarker } from 'react-leaflet';
import { boundsOf } from '../lib/geo';

// Custom marker icons using inline SVG data-uris — no default asset dependency.
const iconStart = L.divIcon({
  className: 'trailback-marker',
  html: '<div style="width:16px;height:16px;border-radius:50%;background:#06D6A0;border:3px solid #0A0A0A;box-shadow:0 0 0 2px #06D6A0"></div>',
  iconSize: [16, 16], iconAnchor: [8, 8],
});
const iconEnd = L.divIcon({
  className: 'trailback-marker',
  html: '<div style="width:16px;height:16px;border-radius:50%;background:#FF9F1C;border:3px solid #0A0A0A;box-shadow:0 0 0 2px #FF9F1C"></div>',
  iconSize: [16, 16], iconAnchor: [8, 8],
});

function FitBounds({ points, follow, center }) {
  const map = useMap();
  useEffect(() => {
    if (follow && center) {
      map.setView([center.lat, center.lng], Math.max(map.getZoom(), 16), { animate: true });
      return;
    }
    if (points && points.length >= 2) {
      const b = boundsOf(points);
      if (b) map.fitBounds([[b.minLat, b.minLng], [b.maxLat, b.maxLng]], { padding: [30, 30] });
    } else if (points && points.length === 1) {
      map.setView([points[0].lat, points[0].lng], 16);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [follow, center?.lat, center?.lng, points?.length]);
  return null;
}

function waypointIcon(color) {
  return L.divIcon({
    className: 'trailback-waypoint',
    html: `<div style="width:14px;height:14px;border-radius:50%;background:${color};border:2px solid #0A0A0A;box-shadow:0 0 0 2px ${color}66"></div>`,
    iconSize: [14, 14], iconAnchor: [7, 7],
  });
}

/* Leaflet-based online map. Falls back to canvas view via parent when offline. */
export default function LeafletMap({ points = [], current = null, follow = true, waypoints = [], height = '100%' }) {
  const startPt = points[0];
  const endPt = points.length > 1 ? points[points.length - 1] : null;
  const initialCenter = current || startPt || { lat: 27.9881, lng: 86.9250 };
  return (
    <div className="w-full h-full rounded-2xl overflow-hidden" style={{ height }}>
      <MapContainer
        center={[initialCenter.lat, initialCenter.lng]}
        zoom={16}
        scrollWheelZoom
        className="w-full h-full"
        preferCanvas
      >
        <TileLayer
          attribution='&copy; OpenStreetMap contributors'
          url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        {points.length > 1 && (
          <Polyline
            positions={points.map((p) => [p.lat, p.lng])}
            pathOptions={{ color: '#FF9F1C', weight: 4, opacity: 0.95 }}
          />
        )}
        {startPt && <Marker position={[startPt.lat, startPt.lng]} icon={iconStart} />}
        {endPt && <Marker position={[endPt.lat, endPt.lng]} icon={iconEnd} />}
        {waypoints.map((w) => (
          <Marker key={w.id} position={[w.lat, w.lng]} icon={waypointIcon(w.color || '#FFD166')} title={w.name} />
        ))}
        {current && (
          <CircleMarker
            center={[current.lat, current.lng]}
            radius={8}
            pathOptions={{ color: '#118AB2', fillColor: '#118AB2', fillOpacity: 1, weight: 3 }}
          />
        )}
        <FitBounds points={points} follow={follow} center={current} />
      </MapContainer>
    </div>
  );
}
